/**
 * PreviewableDropdown.tsx - MS Word-like Dropdown with Live Preview
 * 
 * A dropdown component that:
 * 1. Does NOT steal focus from the editor (selection persists)
 * 2. Shows live preview on hover
 * 3. Commits only on click
 * 4. Reverts on Escape or click-outside
 * 
 * Uses Radix UI primitives but prevents focus theft via onMouseDown.preventDefault()
 * 
 * @author EdgeLex LLC
 * @date 2026-01-24
 */

import * as React from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { useToolbarContextSafe, type ParagraphRange } from './ToolbarContext';
import { OrchestratorLogger } from '../../../../utils/loggers/OrchestratorLogger';

// ============================================================================
// Types
// ============================================================================

export interface DropdownOption<T = string> {
  value: T;
  label: string;
  description?: string;
}

export interface PreviewableDropdownProps<T = string> {
  /** Current value */
  value: T;
  /** Available options */
  options: DropdownOption<T>[];
  /** Called when user commits to a value (click) */
  onCommit: (value: T, ranges: ParagraphRange[]) => Promise<void>;
  /** Called when user hovers over an option (preview) */
  onPreview?: (value: T, ranges: ParagraphRange[]) => Promise<void>;
  /** Called when dropdown closes without selection */
  onCancel?: (originalValue: T, ranges: ParagraphRange[]) => Promise<void>;
  /** Trigger width class */
  triggerWidth?: string;
  /** Aria label */
  ariaLabel?: string;
  /** Whether menu should fit content */
  menuFit?: boolean;
  /** Custom render for items */
  renderItem?: (option: DropdownOption<T>) => React.ReactNode;
  /** Formatting property name (for preview tracking) */
  property?: string;
  /** Whether this control affects selected text (vs document-level setting) */
  affectsSelection?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function PreviewableDropdown<T extends string | number>({
  value,
  options,
  onCommit,
  onPreview,
  onCancel,
  triggerWidth = 'w-[140px]',
  ariaLabel,
  menuFit = false,
  renderItem,
  property = 'unknown',
  affectsSelection = true,
}: PreviewableDropdownProps<T>) {
  const ctx = useToolbarContextSafe();
  
  // Track original value when dropdown opens
  const originalValueRef = React.useRef<T>(value);
  const currentPreviewRef = React.useRef<T | null>(null);
  const rangesRef = React.useRef<ParagraphRange[]>([]);
  const isOpenRef = React.useRef(false);
  // 🔧 FIX (2026-01-25): Track when commit is in progress to prevent race condition
  // Radix may call handleOpenChange(false) BEFORE async onCommit completes
  const isCommittingRef = React.useRef(false);
  // 🔧 FIX (2026-01-25): Track last committed selection to detect stale selection state
  // When clicking on empty areas, the editor doesn't update selection, causing stale data
  const lastCommittedSelectionRef = React.useRef<{ anchor: number; focus: number } | null>(null);
  
  // State for controlled open
  const [isOpen, setIsOpen] = React.useState(false);
  
  /**
   * Handle dropdown open - bind selection
   */
  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsOpen(open);
    isOpenRef.current = open;
    
    if (open) {
      // Dropdown opening - bind selection
      originalValueRef.current = value;
      currentPreviewRef.current = null;

      if (ctx && affectsSelection) {
        ctx.bindSelection();

        // 🔧 FIX (2026-01-25): Clear ranges immediately to prevent stale ranges from previous session
        // This ensures hover events before rAF fires don't use old ranges
        rangesRef.current = [];

        // 🔧 FIX (2026-01-25): Get selection and verify it's not stale from previous commit
        // When clicking on empty areas, the editor doesn't update selection state,
        // so we need to detect if the current selection matches the last committed one
        const selection = ctx.callbacks?.getSelection();

        const anchorOffset = selection?.anchor?.logical?.charOffset ?? 0;
        const focusOffset = selection?.focus?.logical?.charOffset ?? 0;
        const isCollapsed = selection?.isCollapsed ?? true;

        // 🔧 FIX (2026-01-25): Check if selection matches last committed selection (stale detection)
        // If user clicked on empty area, the editor doesn't update selection, leaving stale data
        const lastCommitted = lastCommittedSelectionRef.current;
        const isStaleSelection = lastCommitted !== null &&
          anchorOffset === lastCommitted.anchor &&
          focusOffset === lastCommitted.focus;

        // 🔬 DEBUG: Trace selection state when dropdown opens
        OrchestratorLogger.toolbar.dropdownOpen(property, value, 0, false, {
          debugPhase: 'selection_check',
          hasSelection: !!selection,
          isCollapsed,
          isStaleSelection,
          anchorOffset,
          focusOffset,
          lastCommittedAnchor: lastCommitted?.anchor,
          lastCommittedFocus: lastCommitted?.focus,
          anchorPara: selection?.anchor?.logical?.paraIndex,
          focusPara: selection?.focus?.logical?.paraIndex
        });

        // Only use ranges if selection is non-collapsed AND not stale from previous commit
        if (selection && !isCollapsed && !isStaleSelection) {
          rangesRef.current = ctx.getParagraphRanges(selection);
          // Clear the last committed selection since we have a fresh one
          lastCommittedSelectionRef.current = null;
        } else {
          rangesRef.current = [];
        }

        OrchestratorLogger.toolbar.dropdownOpen(property, value, rangesRef.current.length, !isCollapsed && !isStaleSelection, {
          hasContext: !!ctx,
          hasCallbacks: !!ctx.callbacks,
          debugPhase: 'ranges_set',
          rejectedBecause: isStaleSelection ? 'stale_selection' : (isCollapsed ? 'collapsed' : null)
        });
      } else {
        OrchestratorLogger.toolbar.rangesNone('no context for ' + property, { affectsSelection });
      }
    } else {
      // Dropdown closing
      // 🔧 FIX (2026-01-25): Don't cancel if a commit is in progress
      // Radix calls handleOpenChange(false) BEFORE async handleValueChange completes
      // Without this check, we'd revert the formatting that's being committed
      if (isCommittingRef.current) {
        OrchestratorLogger.toolbar.commit(property, value, rangesRef.current.length, {
          wasPreviewActive: currentPreviewRef.current !== null,
          commitInProgress: true
        });
        // Cleanup will be done by handleValueChange when it completes
      } else if (currentPreviewRef.current !== null && currentPreviewRef.current !== value) {
        // Closed without committing - revert preview
        OrchestratorLogger.toolbar.cancel(property, originalValueRef.current, rangesRef.current.length, { reason: 'closing without commit' });
        handleCancel();
      } else {
        // Normal close (either committed or no preview)
        OrchestratorLogger.toolbar.commit(property, value, rangesRef.current.length, { wasPreviewActive: currentPreviewRef.current !== null });
        if (ctx) {
          ctx.unbindSelection();
        }
      }
    }
  }, [value, ctx, affectsSelection, property]);
  
  /**
   * Handle hover over an option - trigger preview
   */
  const handleItemHover = React.useCallback(async (optionValue: T) => {
    if (!onPreview || !affectsSelection) {
      OrchestratorLogger.toolbar.previewStart(property, optionValue, 0, { skipped: true, reason: 'no onPreview or !affectsSelection' });
      return;
    }
    if (rangesRef.current.length === 0) {
      OrchestratorLogger.toolbar.rangesNone('no ranges for ' + property);
      return;
    }
    if (optionValue === currentPreviewRef.current) {
      return; // Already previewing this - don't spam logs
    }
    
    currentPreviewRef.current = optionValue;
    
    OrchestratorLogger.toolbar.previewStart(property, optionValue, rangesRef.current.length);
    
    try {
      await onPreview(optionValue, rangesRef.current);
    } catch (err) {
      OrchestratorLogger.toolbar.rangesNone('preview error: ' + (err instanceof Error ? err.message : String(err)));
    }
  }, [onPreview, affectsSelection, property]);
  
  /**
   * Handle selection - commit the value
   */
  const handleValueChange = React.useCallback(async (newValue: string) => {
    const typedValue = (typeof value === 'number' ? Number(newValue) : newValue) as T;

    // 🔧 FIX (2026-01-25): Mark commit in progress BEFORE async work
    // This prevents handleOpenChange from calling handleCancel during commit
    isCommittingRef.current = true;

    // 🔧 FIX (2026-01-25): Store the current selection before commit
    // This lets us detect stale selection state on next dropdown open
    const currentSelection = ctx?.callbacks?.getSelection();
    if (currentSelection && rangesRef.current.length > 0) {
      lastCommittedSelectionRef.current = {
        anchor: currentSelection.anchor?.logical?.charOffset ?? 0,
        focus: currentSelection.focus?.logical?.charOffset ?? 0
      };
    }

    OrchestratorLogger.toolbar.commit(property, typedValue, rangesRef.current.length, {
      wasPreviewingThis: currentPreviewRef.current === typedValue
    });

    // If we were previewing this value, it's already applied
    // If not, apply it now
    try {
      await onCommit(typedValue, rangesRef.current);
    } catch (err) {
      OrchestratorLogger.toolbar.rangesNone('commit error: ' + (err instanceof Error ? err.message : String(err)));
    }

    // Clean up
    currentPreviewRef.current = null;
    rangesRef.current = [];
    isCommittingRef.current = false;

    if (ctx) {
      ctx.unbindSelection();
    }
  }, [value, onCommit, ctx, property]);
  
  /**
   * Handle cancel - revert to original
   */
  const handleCancel = React.useCallback(async () => {
    if (!onCancel) {
      if (ctx) ctx.unbindSelection();
      return;
    }
    
    const original = originalValueRef.current;
    
    if (currentPreviewRef.current !== null && currentPreviewRef.current !== original) {
      OrchestratorLogger.toolbar.cancel(property, original, rangesRef.current.length, { 
        previewedValue: currentPreviewRef.current
      });
      
      try {
        await onCancel(original, rangesRef.current);
      } catch (err) {
        OrchestratorLogger.toolbar.rangesNone('cancel error: ' + (err instanceof Error ? err.message : String(err)));
      }
    }
    
    currentPreviewRef.current = null;
    rangesRef.current = [];
    
    if (ctx) {
      ctx.unbindSelection();
    }
  }, [onCancel, ctx, property]);
  
  /**
   * Prevent focus theft - CRITICAL for MS Word-like behavior
   */
  const preventFocusTheft = React.useCallback((e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
  }, []);
  
  /**
   * Handle keyboard in dropdown
   */
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isOpenRef.current) {
      e.preventDefault();
      handleCancel();
      setIsOpen(false);
    }
  }, [handleCancel]);
  
  // Find current option for display
  const currentOption = options.find(o => String(o.value) === String(value));
  const displayLabel = currentOption?.label ?? String(value);
  
  return (
    <RadixSelect.Root
      value={String(value)}
      onValueChange={handleValueChange}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <RadixSelect.Trigger
        aria-label={ariaLabel || displayLabel}
        className={`edg-trigger inline-flex h-7 items-center justify-between rounded px-2 text-[13px] text-gray-900 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10 ${triggerWidth}`}
        onMouseDown={preventFocusTheft}
        onPointerDown={preventFocusTheft}
      >
        <span className="edg-trigger-text flex-1 min-w-0 text-gray-900 dark:text-slate-100 truncate">
          {displayLabel}
        </span>
        <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-70" />
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={6}
          className={`edg-dd z-[1000] rounded-md bg-white dark:bg-slate-800 ring-1 ring-black/10 shadow-lg ${menuFit ? 'edg-dd--fit' : 'edg-dd--match'}`}
          onKeyDown={handleKeyDown}
          // NOTE: Do NOT call preventDefault on Content - it blocks item selection!
          // Focus theft prevention is handled on the Trigger only
        >
          <RadixSelect.Viewport className="edg-dd-viewport p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={String(option.value)}
                value={String(option.value)}
                className="edg-dd-item relative flex items-center px-6 py-1.5 text-[13px] text-gray-900 dark:text-slate-100 outline-none rounded cursor-pointer select-none data-[highlighted]:bg-lex-accent-soft"
                onMouseEnter={() => handleItemHover(option.value)}
                onFocus={() => handleItemHover(option.value)}
              >
                <RadixSelect.ItemIndicator className="absolute left-1 inline-flex items-center">
                  <Check className="h-4 w-4" />
                </RadixSelect.ItemIndicator>
                <RadixSelect.ItemText>
                  {renderItem ? renderItem(option) : (
                    <span className="flex items-center gap-2">
                      <span>{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-gray-500">{option.description}</span>
                      )}
                    </span>
                  )}
                </RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

// ============================================================================
// Specialized Dropdowns
// ============================================================================

/**
 * Font Size Dropdown with live preview
 */
export interface FontSizeDropdownProps {
  value: number;
  onChange: (size: number, ranges: ParagraphRange[]) => Promise<void>;
  onPreview?: (size: number, ranges: ParagraphRange[]) => Promise<void>;
  onCancel?: (originalSize: number, ranges: ParagraphRange[]) => Promise<void>;
}

const FONT_SIZE_OPTIONS: DropdownOption<number>[] = [
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
  { value: 11, label: '11' },
  { value: 12, label: '12' },
  { value: 14, label: '14' },
  { value: 16, label: '16' },
  { value: 18, label: '18' },
  { value: 20, label: '20' },
  { value: 24, label: '24' },
  { value: 28, label: '28' },
  { value: 32, label: '32' },
  { value: 36, label: '36' },
  { value: 48, label: '48' },
  { value: 72, label: '72' },
];

export function FontSizeDropdown({
  value,
  onChange,
  onPreview,
  onCancel,
}: FontSizeDropdownProps) {
  return (
    <PreviewableDropdown<number>
      value={value}
      options={FONT_SIZE_OPTIONS}
      onCommit={onChange}
      onPreview={onPreview}
      onCancel={onCancel}
      triggerWidth="w-[56px]"
      ariaLabel="Font size"
      menuFit
      property="fontSizePx"
      renderItem={(opt) => (
        <span style={{ fontSize: `${Math.min(opt.value, 24)}px` }}>{opt.label}</span>
      )}
    />
  );
}

/**
 * Line Spacing Dropdown with live preview
 */
export interface LineSpacingDropdownProps {
  value: number;
  onChange: (spacing: number, ranges: ParagraphRange[]) => Promise<void>;
  onPreview?: (spacing: number, ranges: ParagraphRange[]) => Promise<void>;
  onCancel?: (originalSpacing: number, ranges: ParagraphRange[]) => Promise<void>;
}

const LINE_SPACING_OPTIONS: DropdownOption<number>[] = [
  { value: 1.0, label: '1.0', description: 'Single' },
  { value: 1.15, label: '1.15', description: '' },
  { value: 1.5, label: '1.5', description: '' },
  { value: 2.0, label: '2.0', description: 'Double' },
  { value: 2.5, label: '2.5', description: '' },
  { value: 3.0, label: '3.0', description: '' },
];

export function LineSpacingDropdown({
  value,
  onChange,
  onPreview,
  onCancel,
}: LineSpacingDropdownProps) {
  return (
    <PreviewableDropdown<number>
      value={value}
      options={LINE_SPACING_OPTIONS}
      onCommit={onChange}
      onPreview={onPreview}
      onCancel={onCancel}
      triggerWidth="w-[72px]"
      ariaLabel="Line spacing"
      menuFit
      property="lineSpacing"
    />
  );
}
